<template><div><p class="d-docsite--paragraph">Size utilities set both <code v-pre>inline-size</code> (width) and <code v-pre>block-size</code> (height) at once. Useful for icons, avatars, thumbnails, and any element that needs to be square or have matching dimensions.</p>
<h2 id="layout-stops" tabindex="-1" class="d-docsite--header-2">Layout stops <a class="header-anchor d-link d-docsite--link d-link" href="#layout-stops" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-size-{stop}</code> to set both width and height using layout token stops. The hyphen before the number indicates a layout token reference, e.g. <code v-pre>d-size-100</code> outputs both <code v-pre>inline-size: var(--dt-layout-100)</code> and <code v-pre>block-size: var(--dt-layout-100)</code> (64px).</p>
<dt-notice kind="info" title="Scale-indexed vs pixel-indexed stops" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Bare integer stops (<code v-pre>25</code>, <code v-pre>50</code>, <code v-pre>100</code>, …) are scale-indexed on the 64px base — <code v-pre>value_in_px = stop × 64 / 100</code>, so <code v-pre>25</code> = 16px and <code v-pre>100</code> = 64px. Stops with a <code v-pre>px</code> suffix (<code v-pre>1px</code>, <code v-pre>2px</code>, <code v-pre>8px</code>, <code v-pre>20px</code>, <code v-pre>24px</code>) are off-scale exceptions that encode the literal pixel value.
</dt-notice>
<code-example source-code='&lt;div class=&quot;d-size-100&quot;&gt;...&lt;/div&gt;  &lt;!-- 64px square --&gt;
&lt;div class=&quot;d-size-200&quot;&gt;...&lt;/div&gt;  &lt;!-- 128px square --&gt;
&lt;div class=&quot;d-size-400&quot;&gt;...&lt;/div&gt;  &lt;!-- 256px square --&gt;'>
<div v-dt-scrollbar:never class="d-bar-400 d-d-flex d-bgc-secondary d-w100p d-hmx-500 d-ta-center">
  <dt-stack gap="100" align="start">
    <div v-for="(i, index) in layout" v-dt-tooltip="{ message: `${i.px}px`, delay: false }">
      <dt-text kind="code" size="100" class="d-w-100 d-us-all">d-size-{{i.stop}}</dt-text>
      <div class="d-h-100 d-bgc-moderate d-bar-300" :class="`d-size-${i.stop}`"></div>
    </div>
  </dt-stack>
</div>
</code-example>
<h2 id="percentages" tabindex="-1" class="d-docsite--header-2">Percentages <a class="header-anchor d-link d-docsite--link d-link" href="#percentages" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-size{n}p</code> to set both width and height to the same percentage. No hyphen before the number, <code v-pre>p</code> suffix indicates a literal percentage value. Note: <code v-pre>d-size33p</code> = 33.333% and <code v-pre>d-size66p</code> = 66.667%.</p>
<code-example only-show="code" source-code='&lt;div class=&quot;d-size50p&quot;&gt;...&lt;/div&gt;   &lt;!-- 50% width and height --&gt;
&lt;div class=&quot;d-size100p&quot;&gt;...&lt;/div&gt;  &lt;!-- 100% width and height --&gt;'>
<div class="d-size50p">...</div>   <!-- 50% width and height -->
<div class="d-size100p">...</div>  <!-- 100% width and height -->
</code-example>
<h2 id="viewport" tabindex="-1" class="d-docsite--header-2">Viewport <a class="header-anchor d-link d-docsite--link d-link" href="#viewport" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use viewport utilities to size an element relative to the viewport in both dimensions. The dynamic viewport variants adapt to mobile browser chrome changes.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>dvh/dvw</strong> (dynamic) -- adapts to mobile browser chrome appearing/disappearing.</li>
<li class="d-docsite--list-element"><strong>svh/svw</strong> (small) -- minimum viewport size, when mobile browser chrome is visible.</li>
<li class="d-docsite--list-element"><strong>lvh/lvw</strong> (large) -- maximum viewport size, when mobile browser chrome is hidden.</li>
</ul>
<code-example only-show="code" source-code='&lt;div class=&quot;d-size-dvh&quot;&gt;...&lt;/div&gt;   &lt;!-- inline-size: 100dvw; block-size: 100dvh --&gt;
&lt;div class=&quot;d-size-svh&quot;&gt;...&lt;/div&gt;   &lt;!-- inline-size: 100svw; block-size: 100svh --&gt;
&lt;div class=&quot;d-size-lvh&quot;&gt;...&lt;/div&gt;   &lt;!-- inline-size: 100lvw; block-size: 100lvh --&gt;'>
<div class="d-size-dvh">...</div>   <!-- inline-size: 100dvw; block-size: 100dvh -->
<div class="d-size-svh">...</div>   <!-- inline-size: 100svw; block-size: 100svh -->
<div class="d-size-lvh">...</div>   <!-- inline-size: 100lvw; block-size: 100lvh -->
</code-example>
<h2 id="keywords" tabindex="-1" class="d-docsite--header-2">Keywords <a class="header-anchor d-link d-docsite--link d-link" href="#keywords" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use keyword utilities to set both width and height using CSS keyword values.</p>
<code-example only-show="code" source-code='&lt;div class=&quot;d-size-auto&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-size-unset&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-size-fit-content&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-size-max-content&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-size-min-content&quot;&gt;...&lt;/div&gt;'>
<div class="d-size-auto">...</div>
<div class="d-size-unset">...</div>
<div class="d-size-fit-content">...</div>
<div class="d-size-max-content">...</div>
<div class="d-size-min-content">...</div>
</code-example>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-ibs-0">
        <tr>
          <th scope="col" class="d-p-0 d-bbw0 d-w30p"><div class="d-p-200 d-bb d-bbw1">Class</div></th>
          <th scope="col" class="d-p-0 d-bbw0"><div class="d-p-200 d-bb d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="i in layout">
          <th scope="row" class="d-code--sm d-docsite-code">.d-size-{{ i.stop }}</th>
          <td class="d-code--sm">inline-size: var(--dt-layout-{{ i.stop }}) !important; block-size: var(--dt-layout-{{ i.stop }}) !important; <span class="d-fc-tertiary">/* {{ i.px }}px / {{ (i.px / 10).toFixed(1) }}rem */</span></td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in percentage">
          <th scope="row" class="d-code--sm d-docsite-code">.d-size{{ i }}p</th>
          <td class="d-code--sm">inline-size: {{ i === 33 ? '33.333' : i === 66 ? '66.667' : i }}% !important; block-size: {{ i === 33 ? '33.333' : i === 66 ? '66.667' : i }}% !important;</td>
        </tr>
      </tbody>
      <tbody>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">.d-size-dvh</th>
          <td class="d-code--sm">inline-size: 100dvw !important; block-size: 100dvh !important;</td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">.d-size-svh</th>
          <td class="d-code--sm">inline-size: 100svw !important; block-size: 100svh !important;</td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">.d-size-lvh</th>
          <td class="d-code--sm">inline-size: 100lvw !important; block-size: 100lvh !important;</td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in keywords">
          <th scope="row" class="d-code--sm d-docsite-code">.d-size-{{ i }}</th>
          <td class="d-code--sm">inline-size: {{ i }} !important; block-size: {{ i }} !important;</td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
</div></template>


<script setup>
  import { percentage, layout, viewport, keywords } from '@data/width-height.json';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';
</script>